<template>
  <b-card>
    <!-- Filters Section -->
    <b-row class="mb-2">
      <b-col md="3">
        <b-form-group label="State">
          <b-form-select
            v-model="filters.state"
            :options="stateOptions"
            @change="onFilterChange"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Start Date">
          <b-form-input
            v-model="filters.startDate"
            type="date"
            @change="onFilterChange"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="End Date">
          <b-form-input
            v-model="filters.endDate"
            type="date"
            @change="onFilterChange"
          />
        </b-form-group>
      </b-col>
      <b-col md="3" class="d-flex align-items-end mb-1">
        <b-button variant="outline-secondary" @click="resetFilters">
          Reset Filters
        </b-button>
      </b-col>
    </b-row>

    <!-- Table Section -->
    <base-overlay :show="loading">
      <b-table
        :fields="tableColumns"
        :items="destinations"
        responsive
        empty-text="No destinations found for this driver"
        show-empty
      >
        <template #cell(destination)="data">
          <div v-if="data.value">
            <div class="d-flex align-items-center">
              <span class="mr-1"><strong>Lat:</strong> {{ data.value.lat }}</span>
              <feather-icon
                icon="CopyIcon"
                size="14"
                class="cursor-pointer text-primary"
                @click="copyToClipboard(`${data.value.lat}, ${data.value.lon}`)"
              />
            </div>
            <div><strong>Lon:</strong> {{ data.value.lon }}</div>
            <div v-if="data.value.address" class="text-muted small">
              {{ data.value.address }}
            </div>
          </div>
        </template>
        <template #cell(driverCurrentLocation)="data">
          <div v-if="data.value">
            <div class="d-flex align-items-center">
              <span class="mr-1"><strong>Lat:</strong> {{ data.value.latitude }}</span>
              <feather-icon
                icon="CopyIcon"
                size="14"
                class="cursor-pointer text-primary"
                @click="copyToClipboard(`${data.value.latitude}, ${data.value.longitude}`)"
              />
            </div>
            <div><strong>Lon:</strong> {{ data.value.longitude }}</div>
            <div v-if="data.value.address" class="text-muted small">
              {{ data.value.address }}
            </div>
          </div>
          <span v-else class="text-muted">N/A</span>
        </template>
        <template #cell(state)="data">
          <b-badge :variant="getStateVariant(data.value)">
            {{ formatState(data.value) }}
          </b-badge>
        </template>
        <template #cell(createdAtFormatted)="data">
          {{ formatDateWithAMPM(data.value) }}
        </template>
        <template #cell(expiresAtFormatted)="data">
          {{ formatDateWithAMPM(data.value) }}
        </template>
      </b-table>

      <!-- Custom Pagination for nextToken -->
      <b-row class="mt-2">
        <b-col md="6" class="d-flex align-items-center">
          <span class="text-muted">
            Showing {{ destinations.length }} destination(s)
            <span v-if="totalCount > 0">
              ({{ totalCount }} in current page)</span
            >
            <span v-if="hasMoreData"> - More available</span>
          </span>
        </b-col>
        <b-col md="6" class="d-flex justify-content-end">
          <b-button
            v-if="paginationHistory.length > 1"
            variant="outline-primary"
            class="mr-1"
            :disabled="loading || paginationHistory.length <= 1"
            @click="loadPreviousPage"
          >
            <feather-icon icon="ChevronLeftIcon" size="18" />
            Previous
          </b-button>
          <b-button
            v-if="hasMoreData"
            variant="primary"
            :disabled="loading || !hasMoreData"
            @click="loadNextPage"
          >
            Next
            <feather-icon icon="ChevronRightIcon" size="18" />
          </b-button>
        </b-col>
      </b-row>
    </base-overlay>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BButton,
  BTable,
  BBadge,
} from "bootstrap-vue";
import { ref, computed, watch, onMounted } from "@vue/composition-api";
import BaseOverlay from "@/UI/BaseOverlay.vue";
import axiosIns from "@/libs/axios";
import formatDate from "@/utils/date-format";
import snakeCaseToCamelCase from "@/utils/snake-case-to-camcel-caase";
import { errorNotification } from "@/utils/toast-notifications";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BaseOverlay,
  },
  props: {
    driverId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const loading = ref(false);
    const destinations = ref([]);
    const nextToken = ref(null);
    const hasMore = ref(false);
    const totalCount = ref(0);
    const paginationHistory = ref([null]); // Track pagination tokens for back navigation
    const currentPageIndex = ref(0);

    const filters = ref({
      state: null,
      startDate: null,
      endDate: null,
    });

    const stateOptions = [
      { value: null, text: "All" },
      { value: "active", text: "Active" },
      { value: "inactive", text: "Inactive" },
    ];

    const tableColumns = [
      {
        key: "destination",
        label: "Destination",
      },
      {
        key: "driverCurrentLocation",
        label: "Driver Location",
      },
      {
        key: "state",
        label: "State",
      },
      {
        key: "createdAtFormatted",
        label: "Created At",
      },
      {
        key: "expiresAtFormatted",
        label: "Expires At",
      },
    ];

    const hasMoreData = computed(() => hasMore.value === true);

    const fetchDestinations = async (token = null) => {
      loading.value = true;
      try {
        const params = {
          driverId: props.driverId,
        };

        if (filters.value.state) {
          params.state = filters.value.state;
        }

        if (filters.value.startDate) {
          params.startDate = filters.value.startDate;
        }

        if (filters.value.endDate) {
          params.endDate = filters.value.endDate;
        }

        if (token) {
          params.nextToken = token;
        }

        const response = await axiosIns.get("/drivers/destinations", {
          params,
        });

        // The API response has a nested data structure: response.data.data
        const data = response.data.data;

        destinations.value = data.destinations || [];
        nextToken.value = data.nextToken || null;
        hasMore.value = data.hasMore || false;
        totalCount.value = data.count || 0;
      } catch (error) {
        console.error("Error fetching driver destinations:", error);
        errorNotification(
          `Failed to fetch driver destinations: ${error.message}`
        );
        destinations.value = [];
        nextToken.value = null;
        hasMore.value = false;
        totalCount.value = 0;
      } finally {
        loading.value = false;
      }
    };

    const loadNextPage = () => {
      if (nextToken.value) {
        currentPageIndex.value += 1;
        if (paginationHistory.value[currentPageIndex.value] === undefined) {
          paginationHistory.value.push(nextToken.value);
        }
        fetchDestinations(nextToken.value);
      }
    };

    const loadPreviousPage = () => {
      if (currentPageIndex.value > 0) {
        currentPageIndex.value -= 1;
        const token = paginationHistory.value[currentPageIndex.value];
        fetchDestinations(token);
      }
    };

    const onFilterChange = () => {
      // Reset pagination when filters change
      paginationHistory.value = [null];
      currentPageIndex.value = 0;
      nextToken.value = null;
      fetchDestinations();
    };

    const resetFilters = () => {
      filters.value = {
        state: null,
        startDate: null,
        endDate: null,
      };
      onFilterChange();
    };

    const formatState = (value) => snakeCaseToCamelCase(value);

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return "N/A";
      return formatDate(new Date(timestamp).toISOString());
    };

    const formatDateWithAMPM = (dateTimeString) => {
      if (!dateTimeString) return "N/A";
      
      // Parse the string format: "18/01/2026 15:32:43"
      const [datePart, timePart] = dateTimeString.split(' ');
      if (!datePart || !timePart) return dateTimeString;
      
      const [hours, minutes, seconds] = timePart.split(':');
      const hour = parseInt(hours, 10);
      const isPM = hour >= 12;
      const hour12 = hour % 12 || 12; // Convert 0 to 12 for midnight
      const ampm = isPM ? 'PM' : 'AM';
      
      return `${datePart} ${hour12}:${minutes}:${seconds} ${ampm}`;
    };

    const getStateVariant = (state) => {
      switch (state?.toLowerCase()) {
        case "active":
          return "success";
        case "inactive":
          return "secondary";
        default:
          return "info";
      }
    };

    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        // You can add a toast notification here if desired
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    };

    // Watch for driverId changes
    watch(
      () => props.driverId,
      (newDriverId) => {
        if (newDriverId) {
          fetchDestinations();
        }
      },
      { immediate: false }
    );

    // Fetch on mount
    onMounted(() => {
      if (props.driverId) {
        fetchDestinations();
      }
    });

    return {
      loading,
      destinations,
      filters,
      stateOptions,
      tableColumns,
      hasMoreData,
      hasMore,
      nextToken,
      paginationHistory,
      loadNextPage,
      loadPreviousPage,
      onFilterChange,
      resetFilters,
      formatDate,
      formatTimestamp,
      formatDateWithAMPM,
      formatState,
      getStateVariant,
      totalCount,
      copyToClipboard,
    };
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
